import {
    AppConfigService,
    AppTokenService,
    AuthAccessTokenDetails,
    OauthAEMConfig
  } from '@com-delta-idp/mnx-sitewide-shared-core-smart';
  import { HttpClient } from '@angular/common/http';
  import { Observable } from 'rxjs';
  import { shareReplay, switchMap } from 'rxjs/operators';
  import { OAuthFeatureConfig } from '../../models/session.interfaces';
  
  /**
   * Helper class for managing auth token retrieval.
   * Handles fetching auth config from AEM and obtaining access tokens
   * via AppTokenService. Used internally by SessionService.
   */
  export class SessionTokenHelper {
    private _authConfigJson$!: Observable<OauthAEMConfig>;
  
    constructor(
      private _oAuthConfig: OAuthFeatureConfig,
      private appTokenService: AppTokenService,
      private http: HttpClient,
      private appConfig: AppConfigService
    ) {}
  
    /**
     * Gets access token using AppTokenService with app-specific auth config.
     */
    async getAccessToken(): Promise<AuthAccessTokenDetails> {
      return await this.getAuthConfigContent()
        .toPromise()
        .then(async (authConfig) => {
          return this.checkConfigResponse(authConfig);
        })
        .catch(() => {
          return {} as AuthAccessTokenDetails;
        });
    }
  
    /**
     * Checks if token has a valid access_token value.
     */
    checkTokenValue(token: AuthAccessTokenDetails): boolean {
      return !!(token && token.access_token);
    }
  
    /**
     * Gets auth config content for token fetching.
     * If OAUTH_FEATURE_CONFIG is provided, fetches from that URL.
     * This is used for app-specific CLIENT_ID and SCOPE.
     */
    private getAuthConfigContent(): Observable<OauthAEMConfig> {
      if (!this._authConfigJson$) {
        if (this._oAuthConfig?.app && this._oAuthConfig?.endpointKey) {
          // Fetch auth config from the URL specified in OAUTH_FEATURE_CONFIG
          this._authConfigJson$ = this.appConfig
            .getConfigValue(this._oAuthConfig.app, this._oAuthConfig.endpointKey)
            .pipe(
              switchMap((url: string) => {
                return this.http.get<OauthAEMConfig>(url);
              }),
              shareReplay(1)
            );
        } else {
          // Fallback to common auth config
          this._authConfigJson$ = this.appConfig
            .getConfigValue('COMMON', 'APP_AUTH_CONFIG_JSON')
            .pipe(
              switchMap((url: string) => {
                return this.http.get<OauthAEMConfig>(url);
              }),
              shareReplay(1)
            );
        }
      }
      return this._authConfigJson$;
    }
  
    /**
     * Checks if auth config has required values and gets token.
     */
    private async checkConfigResponse(
      authConfig: OauthAEMConfig | undefined
    ): Promise<AuthAccessTokenDetails> {
      if (authConfig?.CLIENT_ID_VALUE) {
        return this.getAppAccessToken(authConfig);
      } else {
        return {} as AuthAccessTokenDetails;
      }
    }
  
    /**
     * Gets access token using AppTokenService.
     */
    private async getAppAccessToken(
      authConfig: OauthAEMConfig
    ): Promise<AuthAccessTokenDetails> {
      return await this.appTokenService
        .getAppTokenWithParams(authConfig.CLIENT_ID_VALUE, authConfig.SCOPE_VALUE)
        .toPromise()
        .then((data) => {
          return data || ({} as AuthAccessTokenDetails);
        })
        .catch(() => {
          return {} as AuthAccessTokenDetails;
        });
    }
  }
  
